import './pull-to-right.scss';

import { defineComponent } from 'vue';

interface Point2D {
    x: number;
    y: number;
}

const PULL_THRESHOLD_IN_PIXEL = 20;

// this is used to detect if a vertical scroll is occuring.
// If this happens we cancel our translation and the browser
// will not trigger further event during the scroll transaction
const VERTICAL_OFFSET_THRESHOLD_IN_PIXEL = 5;

const NON_RETURN_RATIO = 0.8;

export const PullToRight = defineComponent({
    props: ['enabled'],
    template: require('./pull-to-right.html'),
    data() {
        return {
            pulling: false,
            origin: { x: 0, y: 0 } as Point2D,
            offset: { x: 0, y: 0 } as Point2D,
            nonReturnRatioReached: false,
        }
    },
    methods: {
        onTouchStart(event: TouchEvent) {
            if (!this.enabled) {
                return;
            }

            const touch = event.touches[0];
            this.origin = {
                x: touch.screenX,
                y: touch.screenY,
            };
            this.pulling = false;
            this.nonReturnRatioReached = false;
        },
        onTouchMove(event: TouchEvent) {
            if (!this.enabled) {
                return;
            }

            const changedTouch = event.changedTouches[0];
            this.offset = {
                x: changedTouch.screenX - this.origin.x,
                y: changedTouch.screenY - this.origin.y,
            };

            if (
                this.offset.x > PULL_THRESHOLD_IN_PIXEL ||
                (!this.pulling && Math.abs(this.offset.y) > VERTICAL_OFFSET_THRESHOLD_IN_PIXEL)
            ) {
                this.translateX(0);
                return;
            }

            this.pulling = true;

            const offset = Math.abs(Math.min(0, this.offset.x - PULL_THRESHOLD_IN_PIXEL));
            const ratio = offset / this.clientWidth();

            this.nonReturnRatioReached = ratio > NON_RETURN_RATIO;

            const elasticRatio = ratio * Math.pow(ratio, 2);
            const offsetX = -Math.min(this.clientWidth(), elasticRatio * offset);

            this.translateX(offsetX);
        },
        onTouchEnd() {
            if (!this.enabled) {
                return;
            }

            if (this.nonReturnRatioReached) {
                this.translateX(-this.clientWidth());
                this.$emit('pulled');
                return;
            }

            this.translateX(0);
        },
        reset() {
            this.translateX(0);
        },
        translateX(offsetX: number) {
            // The two slotted pages are rendered as direct <div> children of the root; drive their
            // transform straight off the DOM (Vue 3 no longer exposes slot vnodes with a `.elm`).
            const root = this.$el as HTMLElement | null;
            const pages = Array.from(root?.children ?? []).filter(
                (el): el is HTMLElement => el instanceof HTMLElement && el.tagName === 'DIV',
            );
            if (pages.length > 1) {
                pages[0].style.transform = `translate3d(${offsetX}px, 0, 0)`;
                pages[1].style.transform = `translate3d(${offsetX}px, 0, 0)`;
            }
        },
        clientWidth() {
            return (this.$refs.viewport as HTMLDivElement).clientWidth;
        },
    },
})
